/**
 * Navigation & Background & Mobile Menu - Moon Moon Agency
 * Handles navigation, background filters, and mobile menu interactions
 */

/*********************************************************************************************
 * 🎯 NAVIGATION MODULE
 */
const NavigationManager = {
	// Local configuration
	config: {
		selectors: {
			header: 'header',
			megaMenuLink: '[data-megamenulink]',
			menuLink: '[data-menulink]',
			filter: '[data-filter]',
			cartFilter: '[data-drawer-cart]',
			searchContainer: '[data-search-container]',
			headerBurger: '[data-header-burger]',
			menuMobile: '[data-menu-mobile]',
			menuSublinks: '[data-menu-sublinks]',
			accountUpgrade: '[data-account-upgrade]'
		},
		classes: {
			active: 'active',
			open: 'mm-open',
			drawerOpen: 'mm-drawer-open',
			stopScrolling: 'stop-scrolling',
			mobileMenuSlided: 'mm-mobile-menu-elem-slided',
			headerMobileMenuSlided: 'mm-header-mobile-menu-elem-slided',
			megaMenuSwitching: 'mm-header--mega-switching'
		},
		timing: {
			filterHideDelay: 300
		}
	},

	/**
	 * Initialize navigation systems
	 */
	init() {
		this.initMegaMenus();
		this.initBackgroundFilter();
		this.initAccessibilityHandlers();
		this.initMegaMenuAccessibility();
		this.initMobileSubmenuAccessibility();
		this.initMobileDropdownAccessibility();
		this.initMobileAccount();
	},

	/**
	 * Compte du menu mobile : lien simple dans le HTML, remplacé par <shopify-account>
	 * sur les navigateurs récents. Le composant écrit en dur dans le menu mobile fait
	 * planter Safari iOS 17.3 (2026-11-03). Seuil : URL.parse, arrivé avec Safari 18
	 * (iOS 17.4 à 17.7 jamais testés sur appareil, ils gardent le lien). Pas de test CSS :
	 * le WebKit 17.4 de Playwright annonce content-visibility, que Safari 17 n'active pas.
	 */
	initMobileAccount() {
		if (typeof URL.parse !== 'function') return;

		window.getElements(this.config.selectors.accountUpgrade).forEach((link) => {
			const account = document.createElement('shopify-account');
			account.className = link.className;
			if (link.dataset.accountMenu) account.setAttribute('menu', link.dataset.accountMenu);

			const avatar = document.createElement('div');
			avatar.slot = 'signed-out-avatar';
			avatar.append(...link.childNodes);
			account.append(avatar);

			link.replaceWith(account);
		});
	},

	/**
	 * Initialize accessibility keyboard handlers for header buttons
	 */
	initAccessibilityHandlers() {
		// Burger menu button
		const burgerBtn = window.getElement('[data-header-burger]');
		if (burgerBtn) {
			burgerBtn.addEventListener('click', () => {
				window.mmMobileMenu();
				// aria-expanded géré dans MobileMenuManager.toggle()
			});
		}

		// Search toggle button
		const searchBtn = window.getElement('[data-search-toggle]');
		if (searchBtn) {
			searchBtn.addEventListener('click', () => {
				this.closeAllMegaMenus();
				window.mmCloseMobileMenu();
				window.mmShowSearch(searchBtn);
				// aria-expanded géré par SearchManager._updateToggleAria()
			});
		}

		// Cart toggle button
		const cartBtn = window.getElement('[data-cart-toggle]');
		if (cartBtn) {
			cartBtn.addEventListener('click', () => {
				window.mmOpenCart(cartBtn);
				window.mmCloseMobileMenu(true);
				window.mmHideSearch(true);
			});
		}

		// Global Escape key handler (close all overlays)
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const mobileMenu = window.getElement(this.config.selectors.menuMobile);
				if (mobileMenu?.classList.contains(this.config.classes.open)) {
					window.mmCloseMobileMenu();
					burgerBtn?.focus(); // Return focus to burger button
				}
			}
		});

		// Clic hors du header et du menu mobile : ferme le menu. Sans ce handler,
		// le tap déclenchait seulement le mouseleave du header, qui retirait le
		// filtre en laissant le menu ouvert. Phase de capture + blocage du clic,
		// comme pour la recherche (mm_search.js) : ce clic ne fait que fermer.
		document.addEventListener('click', (e) => {
			const mobileMenu = window.getElement(this.config.selectors.menuMobile);
			if (!mobileMenu?.classList.contains(this.config.classes.open)) return;
			if (e.target.closest(`.mm-header, ${this.config.selectors.menuMobile}`)) return;

			e.preventDefault();
			e.stopPropagation();
			window.mmCloseMobileMenu();
		}, true);

		window.log('info', 'NavigationManager', 'Accessibility handlers initialized');
	},

	/**
	 * Initialize mega menu and submenu positioning
	 */
	initMegaMenus() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) {
			window.log('warn', 'NavigationManager', 'Header not found');
			return;
		}

		const headerHeight = header.offsetHeight;

		// Position submenus (data-menulink with nested data-megamenu)
		window.getElements(this.config.selectors.menuLink).forEach((menuElem) => {
			const submenu = menuElem.querySelector('[data-megamenu]');
			if (submenu) {
				submenu.style.top = `${headerHeight - 1}px`;
			}
		});
	},

	/**
	 * Initialize mega menu keyboard accessibility
	 */
	initMegaMenuAccessibility() {
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const regularMenuLinks = window.getElements(this.config.selectors.menuLink);

		// Mega menu links (with dropdown)
		megaMenuLinks.forEach((link) => {
			const megaMenu = link.nextElementSibling;
			if (!megaMenu?.hasAttribute('data-megamenu')) return;

			// Click to toggle mega menu — navigate if already open (desktop hover), otherwise open it first
			link.addEventListener('click', (e) => {
				const isOpen = link.getAttribute('aria-expanded') === 'true';
				if (isOpen) return;
				e.preventDefault();
				this.toggleMegaMenu(link, megaMenu);
			});

			// Keyboard: Enter to toggle, Arrow Down to focus first item
			link.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					this.toggleMegaMenu(link, megaMenu);
				} else if (e.key === 'ArrowDown') {
					e.preventDefault();
					const isOpen = link.getAttribute('aria-expanded') === 'true';
					if (!isOpen) {
						this.openMegaMenu(link, megaMenu);
					}
					// Focus first link in mega menu
					const firstLink = megaMenu.querySelector('a');
					firstLink?.focus();
				}
			});

			// Hover to open — sauf recherche ouverte : le mega menu chevauche alors
			// le panneau de recherche, seul le click doit pouvoir l'ouvrir.
			link.addEventListener('mouseenter', () => {
				const searchContainer = window.getElement(this.config.selectors.searchContainer);
				if (searchContainer?.classList.contains(this.config.classes.open)) return;

				// Un mega menu etait deja ouvert : bascule instantanee entre categories (sans transition).
				// Sinon (menu ferme) : premiere ouverture, animee (fade + translate, voir mm-mega-menu.css)
				const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
				const wasAnyOpen = Array.from(allMegaMenuLinks).some((l) => l.getAttribute('aria-expanded') === 'true');
				const header = window.getElement('.mm-header');
				header?.classList.toggle(this.config.classes.megaMenuSwitching, wasAnyOpen);

				// Close other mega menus (not this one)
				allMegaMenuLinks.forEach((otherLink) => {
					if (otherLink !== link) {
						otherLink.setAttribute('aria-expanded', 'false');
					}
				});
				// Open this mega menu
				this.openMegaMenu(link, megaMenu);
			});
		});

		// Regular menu links and submenus
		regularMenuLinks.forEach((link) => {
			// Only add listener if it's NOT a mega menu link
			if (!link.hasAttribute('data-megamenulink')) {
				const submenu = link.querySelector('[data-megamenu]');

				if (submenu) {
					// This link HAS a submenu - close other mega menus on hover
					link.addEventListener('mouseenter', () => {
						// Close all mega menus (but CSS will handle submenu display)
						const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
						allMegaMenuLinks.forEach((megaLink) => {
							megaLink.setAttribute('aria-expanded', 'false');
						});
						const header = window.getElement('.mm-header');
						header?.classList.remove(this.config.classes.megaMenuSwitching);
						// Show background filter for submenu too
						this.showBackgroundFilter();
					});

					// Hide background filter when leaving submenu — sauf recherche
					// ouverte : le filtre doit rester present derriere elle.
					link.addEventListener('mouseleave', () => {
						const searchContainer = window.getElement(this.config.selectors.searchContainer);
						if (searchContainer?.classList.contains(this.config.classes.open)) return;
						this.hideBackgroundFilter();
					});

					// Clavier : le bouton ouvre/ferme, Echap ferme et rend le focus,
					// sortir du sous-menu au Tab le ferme (WCAG 2.1.1)
					const toggle = link.querySelector('[data-submenu-toggle]');
					if (toggle) {
						const setOpen = (open) => {
							link.classList.toggle('mm-open', open);
							toggle.setAttribute('aria-expanded', String(open));
						};
						toggle.addEventListener('click', () => {
							setOpen(toggle.getAttribute('aria-expanded') !== 'true');
						});
						link.addEventListener('keydown', (e) => {
							if (e.key === 'Escape' && link.classList.contains('mm-open')) {
								setOpen(false);
								toggle.focus();
							}
						});
						link.addEventListener('focusout', (e) => {
							if (!link.contains(e.relatedTarget)) setOpen(false);
						});
					}
				} else {
					// This link has NO dropdown - close all menus on hover
					link.addEventListener('mouseenter', () => {
						this.closeAllMegaMenus();
					});
				}
			}
		});

		// Mouse leave header to close all menus
		const header = window.getElement(this.config.selectors.header);
		if (header) {
			header.addEventListener('mouseleave', () => {
				this.closeAllMegaMenus();
			});
		}

		// Escape key closes all mega menus
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const openLink = Array.from(megaMenuLinks).find(
					(link) => link.getAttribute('aria-expanded') === 'true'
				);
				if (openLink) {
					this.closeAllMegaMenus();
					// Focus rendu au lien parent : sinon il tombe sur body (WCAG 2.4.3)
					openLink.focus();
				}
			}
		});

		window.log('info', 'NavigationManager', 'Mega menu accessibility initialized');
	},

	/**
	 * Toggle mega menu state
	 */
	toggleMegaMenu(link, megaMenu) {
		const isOpen = link.getAttribute('aria-expanded') === 'true';
		if (isOpen) {
			this.closeMegaMenu(link, megaMenu);
		} else {
			this.closeAllMegaMenus(); // Close others first
			this.openMegaMenu(link, megaMenu);
		}
	},

	/**
	 * Open mega menu
	 */
	openMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'true');
		this.showBackgroundFilter();
	},

	/**
	 * Close mega menu
	 */
	closeMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'false');
		const header = window.getElement('.mm-header');
		header?.classList.remove(this.config.classes.megaMenuSwitching);
	},

	/**
	 * Close all mega menus
	 */
	closeAllMegaMenus() {
		const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		allMegaMenuLinks.forEach((link) => {
			link.setAttribute('aria-expanded', 'false');
		});
		const header = window.getElement('.mm-header');
		header?.classList.remove(this.config.classes.megaMenuSwitching);
		// Recherche ouverte : le filtre reste derriere elle (sortie du header,
		// survol d'un lien simple). SearchManager.hide() le retire a la fermeture.
		const searchContainer = window.getElement(this.config.selectors.searchContainer);
		if (searchContainer?.classList.contains(this.config.classes.open)) return;
		this.hideBackgroundFilter();
	},

	/**
	 * Initialize background filter hover behaviors
	 */
	initBackgroundFilter() {
		const elements = {
			header: window.getElement(this.config.selectors.header),
			searchContainer: window.getElement(this.config.selectors.searchContainer),
			filter: window.getElement(this.config.selectors.filter),
			cart: window.getElement(this.config.selectors.cartFilter)
		};

		if (!elements.header || !elements.filter || !elements.cart) {
			window.log('warn', 'NavigationManager', 'Background filter elements not found');
			return;
		}

		const { header, searchContainer, filter, cart } = elements;
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const menuLinks = window.getElements(this.config.selectors.menuLink);

		const toggleFilter = (show) => {
			filter.classList.toggle(this.config.classes.active, show);
			window.emit(`global:background-filter-${show ? 'activated' : 'deactivated'}`, { 
				trigger: show ? 'toggle-on' : 'toggle-off',
				source: 'navigation' 
			});
		};

		// Event listeners
		megaMenuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(true))
		);
		
		menuLinks.forEach((link) =>
			link.addEventListener('mouseenter', () => {
				// Recherche ouverte : le filtre doit rester présent (clic dessus =
				// fermeture de la recherche, géré par SearchManager), même si la
				// souris quitte mm-predictive-search pour survoler un lien simple.
				const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
				if (searchOpen) return;
				toggleFilter(false);
			})
		);

		header.addEventListener('mouseleave', () => {
			const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
			const cartOpen = cart.classList.contains(this.config.classes.drawerOpen);
			
			if (!searchOpen && !cartOpen) {
				toggleFilter(false);
			}
		});

	},

	/**
	 * Show background filter
	 */
	showBackgroundFilter(zIndex) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) {
			window.log('warn', 'NavigationManager', 'Background filter element not found');
			return;
		}
		
		filter.classList.add(this.config.classes.active);
		if (zIndex) filter.style.zIndex = zIndex;
		window.emit('global:background-filter-activated', { 
			trigger: 'show', 
			zIndex, 
			hasZIndex: !!zIndex 
		});
	},

	/**
	 * Hide background filter
	 */
	hideBackgroundFilter() {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;
		
		setTimeout(() => filter.style.removeProperty('z-index'), this.config.timing.filterHideDelay);
		filter.classList.remove(this.config.classes.active);
		window.emit('global:background-filter-deactivated', { 
			trigger: 'hide',
			delay: this.config.timing.filterHideDelay 
		});
	},

	/**
	 * Initialize mobile submenu accessibility (replace inline onclick)
	 */
	initMobileSubmenuAccessibility() {
		// Submenu trigger buttons (show next)
		const submenuTriggers = window.getElements('[data-mobile-submenu-trigger]');
		submenuTriggers.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuTrigger;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowNext(targetSelector);
				btn.setAttribute('aria-expanded', 'true');
				// Le déclencheur est recouvert par le sous-menu : le focus passe sur son bouton retour
				window.getElement(targetSelector)?.querySelector('[data-mobile-submenu-back]')?.focus();
			});
		});

		// Submenu back buttons (show prev)
		const submenuBackBtns = window.getElements('[data-mobile-submenu-back]');
		submenuBackBtns.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuBack;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowPrev(targetSelector);
				// Find corresponding trigger and update aria-expanded
				const trigger = window.getElement(`[data-mobile-submenu-trigger="${targetSelector}"]`);
				if (trigger) {
					trigger.setAttribute('aria-expanded', 'false');
					trigger.focus();
				}
			});
		});

		window.log('info', 'NavigationManager', 'Mobile submenu accessibility initialized');
	},

	/**
	 * Initialize mobile inline dropdowns (sous-sous-liens du menu mobile)
	 */
	initMobileDropdownAccessibility() {
		const dropdownTriggers = window.getElements('[data-mobile-dropdown-trigger]');
		dropdownTriggers.forEach((btn) => {
			btn.addEventListener('click', () => {
				const isOpen = btn.getAttribute('aria-expanded') === 'true';
				btn.setAttribute('aria-expanded', String(!isOpen));
			});
		});

		window.log('info', 'NavigationManager', 'Mobile dropdown accessibility initialized');
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		this.initMegaMenus();
	}
};

/*********************************************************************************************
 * 📱 MOBILE MENU MODULE
 */
const MobileMenuManager = {
	// Reference to NavigationManager config
	config: NavigationManager.config,

	// Focus trap state
	focusTrap: {
		isActive: false,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null,
		triggerElement: null
	},

	/**
	 * Setup focus trap for mobile menu
	 */
	setupFocusTrap(mobileMenu) {
		// Retire un trap encore actif (double open sans close) : sinon la reference
		// handleKeyDown est ecrasee et l'ancien listener devient impossible a retirer.
		this.removeFocusTrap();

		// Store trigger element to restore focus on close
		this.focusTrap.triggerElement = document.activeElement;

		// Bornes recalculées à chaque Tab : les sous-panneaux fermés sont en
		// visibility: hidden, donc la liste des éléments atteignables change.
		const updateBounds = () => {
			const focusableElements = [...mobileMenu.querySelectorAll(window.FOCUSABLE_SELECTOR)]
				.filter((el) => el.checkVisibility?.({ visibilityProperty: true }) ?? true);
			this.focusTrap.firstFocusable = focusableElements[0] || null;
			this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1] || null;
		};

		updateBounds();
		if (!this.focusTrap.firstFocusable) return;

		// Handle Tab key to trap focus
		this.focusTrap.handleKeyDown = (e) => {
			if (e.key !== 'Tab') return;
			updateBounds();

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		this.focusTrap.firstFocusable?.focus();

		window.log('info', 'MobileMenuManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;

			// Restore focus to the element that triggered the menu
			if (this.focusTrap.triggerElement) {
				this.focusTrap.triggerElement.focus();
				this.focusTrap.triggerElement = null;
			}

			window.log('info', 'MobileMenuManager', 'Focus trap removed');
		}
	},

	/**
	 * Burger affiché ? Synchro CSS (mm-header.css : width <= 1200px OU tactile)
	 * sans dupliquer le breakpoint côté JS.
	 */
	isBurgerMode() {
		const burger = window.getElement(this.config.selectors.headerBurger);
		return !!burger && getComputedStyle(burger).display !== 'none';
	},

	/**
	 * Toggle mobile menu
	 */
	toggle() {
		if (!this.isBurgerMode()) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const mobileMenuSecondary = window.getElement(this.config.selectors.menuSublinks);
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu || !mobileMenuSecondary) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found');
			return;
		}

		const headerHeight = header.offsetHeight;
		const headerMenuTop = header.getBoundingClientRect().top;
		const topPosition = `${headerHeight + headerMenuTop}px`;
		const heightCalc = `calc(var(--doc-height) - ${topPosition})`;

		[header, icon, mobileMenu].forEach((el) => el.classList.toggle(this.config.classes.open));

		Object.assign(mobileMenu.style, { top: topPosition, height: heightCalc });
		mobileMenuSecondary.style.height = heightCalc;

		const isOpen = mobileMenu.classList.contains(this.config.classes.open);

		// Update burger aria-expanded (centralisé ici pour couvrir tous les cas d'appel)
		icon.setAttribute('aria-expanded', String(isOpen));

		if (isOpen) {
			html.classList.add(this.config.classes.stopScrolling);
			// Use z-index lower than mobile menu (menu is 10, filter should be 5)
			NavigationManager.showBackgroundFilter('var(--index-sm)');
			this.setupFocusTrap(mobileMenu);
			// Inert background content for screen readers (WCAG 2.4.3)
			document.querySelectorAll('main, footer').forEach(el => el.inert = true);
		} else {
			html.classList.remove(this.config.classes.stopScrolling);
			NavigationManager.hideBackgroundFilter();
			this.removeFocusTrap();
			document.querySelectorAll('[inert]').forEach(el => el.inert = false);
			this._resetSubmenus();
		}

		window.emit('global:mobile-menu-toggled', { headerHeight, topPosition });
	},

	/**
	 * Close mobile menu
	 */
	close(keepFilterActive = false) {
		if (!this.isBurgerMode()) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found for closing');
			return;
		}

		// Ne retirer le scroll-lock QUE si le menu était réellement ouvert, pour ne pas
		// retirer .stop-scrolling posée par un autre overlay (ex: cart ouvert qui appelle
		// mmCloseMobileMenu au passage).
		const wasOpen = mobileMenu.classList.contains(this.config.classes.open);
		[header, icon, mobileMenu].forEach((el) => el.classList.remove(this.config.classes.open));
		if (wasOpen) html.classList.remove(this.config.classes.stopScrolling);

		// Update burger aria-expanded
		icon.setAttribute('aria-expanded', 'false');

		// Restore background content
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);

		// Remove focus trap when menu closes
		this.removeFocusTrap();
		this._resetSubmenus();

		if (!keepFilterActive) NavigationManager.hideBackgroundFilter();
		window.emit('global:mobile-menu-closed', { keepFilterActive });
	},

	/**
	 * Referme les sous-menus ouverts : sinon le menu se rouvre sur le sous-menu,
	 * avec aria-expanded resté à true et le header en style « sous-menu ».
	 */
	_resetSubmenus() {
		window.getElements(`.${this.config.classes.mobileMenuSlided}`)
			.forEach((el) => el.classList.remove(this.config.classes.mobileMenuSlided));
		window.getElement('.mm-header')?.classList.remove(this.config.classes.headerMobileMenuSlided);
		window.getElements('[data-mobile-submenu-trigger][aria-expanded="true"]')
			.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
	},

	/**
	 * Show next sub-menu
	 */
	showNext(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (element) {
			element.classList.add(this.config.classes.mobileMenuSlided);
			const header = window.getElement('.mm-header');
			if (header) header.classList.add(this.config.classes.headerMobileMenuSlided);
			window.emit('global:mobile-submenu-next', { selector: elemToShowClass });
		}
	},

	/**
	 * Show previous sub-menu
	 */
	showPrev(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (element) {
			element.classList.remove(this.config.classes.mobileMenuSlided);
			const header = window.getElement('.mm-header');
			if (header) header.classList.remove(this.config.classes.headerMobileMenuSlided);
			window.emit('global:mobile-submenu-prev', { selector: elemToShowClass });
		}
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.NavigationManager = NavigationManager;
window.MobileMenuManager = MobileMenuManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Navigation functions
window.getMegaMenuPosition = () => NavigationManager.initMegaMenus();

// Background filter functions
window.showBgFilter = (zIndex) => NavigationManager.showBackgroundFilter(zIndex);
window.hideBgFilter = () => NavigationManager.hideBackgroundFilter();

// Mobile menu functions
window.mmMobileMenu = () => MobileMenuManager.toggle();
window.mmCloseMobileMenu = (keep) => MobileMenuManager.close(keep);
window.mmMobileMenuShowNext = (elem) => MobileMenuManager.showNext(elem);
window.mmMobileMenuShowPrev = (elem) => MobileMenuManager.showPrev(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	NavigationManager.init();
});

window.addEventListener('resize', window.debounce(() => {
	NavigationManager.onResize();
}, 150));